/**
 * WFA Period Table Mock
 *
 * Mirrors the WFAPeriodTable component — a walk-by-walk breakdown
 * showing in-sample and out-of-sample periods, returns, and trade counts.
 */

interface WalkPeriod {
  walk: number;
  isPeriod: string;
  oosPeriod: string;
  isReturn: number;
  oosReturn: number;
  trades: number;
  status: 'pass' | 'warn' | 'fail';
}

const periods: WalkPeriod[] = [
  { walk: 1, isPeriod: 'Jan 23 - Jun 23', oosPeriod: 'Jul 23 - Aug 23', isReturn: 28.4, oosReturn: 12.1, trades: 14, status: 'pass' },
  { walk: 2, isPeriod: 'Mar 23 - Aug 23', oosPeriod: 'Sep 23 - Oct 23', isReturn: 31.6, oosReturn: 8.7, trades: 11, status: 'pass' },
  { walk: 3, isPeriod: 'May 23 - Oct 23', oosPeriod: 'Nov 23 - Dec 23', isReturn: 22.1, oosReturn: -2.4, trades: 9, status: 'warn' },
  { walk: 4, isPeriod: 'Jul 23 - Dec 23', oosPeriod: 'Jan 24 - Feb 24', isReturn: 35.8, oosReturn: 15.3, trades: 16, status: 'pass' },
  { walk: 5, isPeriod: 'Sep 23 - Feb 24', oosPeriod: 'Mar 24 - Apr 24', isReturn: 19.5, oosReturn: 6.2, trades: 12, status: 'pass' },
  { walk: 6, isPeriod: 'Nov 23 - Apr 24', oosPeriod: 'May 24 - Jun 24', isReturn: 40.2, oosReturn: 22.8, trades: 18, status: 'pass' },
  { walk: 7, isPeriod: 'Jan 24 - Jun 24', oosPeriod: 'Jul 24 - Aug 24', isReturn: 26.9, oosReturn: 9.4, trades: 13, status: 'pass' },
  { walk: 8, isPeriod: 'Mar 24 - Aug 24', oosPeriod: 'Sep 24 - Oct 24', isReturn: 33.1, oosReturn: -4.7, trades: 8, status: 'fail' },
];

const statusIcon = (s: WalkPeriod['status']) => {
  if (s === 'pass') return { char: 'P', bg: 'rgba(34,197,94,0.15)', border: 'rgba(34,197,94,0.3)', color: '#22c55e' };
  if (s === 'warn') return { char: '!', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.3)', color: '#f59e0b' };
  return { char: 'F', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)', color: '#ef4444' };
};

const thStyle: React.CSSProperties = {
  fontSize: '0.5rem',
  color: '#6b7280',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  padding: '0.375rem 0.5rem',
  textAlign: 'right',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const tdStyle: React.CSSProperties = {
  fontSize: '0.5625rem',
  color: '#9ca3af',
  padding: '0.375rem 0.5rem',
  textAlign: 'right',
  fontFamily: 'ui-monospace, monospace',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
};

export default function WFAPeriodTableMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Walk-by-Walk Breakdown
      </div>
      <div className="platform-mock-body" style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
              <th style={{ ...thStyle, textAlign: 'center', width: 30 }}>#</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>In-Sample</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Out-of-Sample</th>
              <th style={thStyle}>IS Return</th>
              <th style={thStyle}>OOS Return</th>
              <th style={thStyle}>Trades</th>
              <th style={{ ...thStyle, textAlign: 'center', width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => {
              const icon = statusIcon(p.status);
              return (
                <tr key={p.walk}>
                  <td style={{ ...tdStyle, textAlign: 'center', color: '#6b7280' }}>{p.walk}</td>
                  <td style={{ ...tdStyle, textAlign: 'left', color: '#93c5fd' }}>{p.isPeriod}</td>
                  <td style={{ ...tdStyle, textAlign: 'left', color: '#c4b5fd' }}>{p.oosPeriod}</td>
                  <td style={{ ...tdStyle, color: p.isReturn >= 0 ? '#22c55e' : '#ef4444' }}>
                    {p.isReturn >= 0 ? '+' : ''}{p.isReturn.toFixed(1)}%
                  </td>
                  <td style={{ ...tdStyle, color: p.oosReturn >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                    {p.oosReturn >= 0 ? '+' : ''}{p.oosReturn.toFixed(1)}%
                  </td>
                  <td style={tdStyle}>{p.trades}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 16, height: 16, borderRadius: '50%',
                      background: icon.bg, border: `1px solid ${icon.border}`,
                      fontSize: '0.5rem', fontWeight: 700, color: icon.color,
                    }}>{icon.char}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="platform-mock-caption">
        Each row is a separate optimization-and-test cycle. Out-of-sample returns tell you whether the optimized parameters generalise.
      </div>
    </div>
  );
}
